<template>
  <Dialog
    v-model:visible="visibleValue"
    modal
    :draggable="false"
    dismissable-mask
    :style="{ width: '670px' }"
    class="invite-members-dialog"
  >
    <template #header>
      <div>
        <p class="p-dialog-title">
          {{
            $t(
              "components.structural.organization.dialog.tabs-content.members-tab.invite.dialog.title",
            )
          }}
        </p>
        <p class="m-0">
          {{
            $t(
              "components.structural.organization.dialog.tabs-content.members-tab.invite.dialog.sub-title",
            )
          }}
        </p>
      </div>
    </template>

    <FormsForm
      :schema="schema"
      :initial-values="initialValues"
      :on-submit="inviteMembers"
      :submit-label="
        $t(
          'components.structural.organization.dialog.tabs-content.members-tab.invite.dialog.form.submit-cta',
        )
      "
    >
      <template #default="{ values }">
        <FieldArray
          v-slot="{ fields, push, remove }"
          name="groupsToInvite"
        >
          <div
            v-for="(field, index) in fields"
            :key="field.key"
            class="mb-2 grid w-full grid-cols-12 gap-3"
          >
            <div
              :class="{
                'col-span-8': fields.length > 1,
                'col-span-9': fields.length === 1,
              }"
            >
              <FormsFieldsAutocomplete
                :name="`groupsToInvite[${index}].emails`"
                :typeahead="false"
                class="mb-0"
              />
            </div>
            <div class="col-span-3">
              <FormsFieldsSelect
                :name="`groupsToInvite[${index}].role`"
                :options="
                  getFilteredOrganizationRolesOptions(
                    values.groupsToInvite,
                    field.value.role,
                  )
                "
                class="mb-0"
              >
                <template #option="{ option }">
                  <div class="flex items-center">
                    <div>{{ option.label }}</div>
                  </div>
                </template>
              </FormsFieldsSelect>
            </div>
            <div
              v-if="fields.length > 1"
              class="col-span-1"
            >
              <Button
                text
                @click="remove(index)"
              >
                <template #icon>
                  <LucideIcon :icon="X" />
                </template>
              </Button>
            </div>
          </div>

          <Button
            :label="
              $t(
                'components.structural.organization.dialog.tabs-content.members-tab.invite.dialog.form.add-another',
              )
            "
            text
            :disabled="
              !getFilteredOrganizationRolesOptions(values.groupsToInvite)
                .length > 0
            "
            @click="
              push({
                emails: [],
                role: getFilteredOrganizationRolesOptions(
                  values.groupsToInvite,
                )[0].value,
              })
            "
          >
            <template #icon>
              <LucideIcon :icon="CirclePlus" />
            </template>
          </Button>
        </FieldArray>
      </template>

      <template #footer>
        <Button
          :label="$t('common.cta.cancel')"
          severity="secondary"
          @click="$emit('hide')"
        />
      </template>
    </FormsForm>
  </Dialog>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { array, number, object, string } from 'yup'
import { CirclePlus, X } from 'lucide-vue-next'
import { FieldArray } from 'vee-validate'
import type { AppConfig } from '~/types/app'
import type { InvitationGroup } from '~/types/organizations/invitation'
import { OrganizationRoles } from '~/constants/organizations'
import type { InviteOrganizationMembersData } from '~/functions/types/organization'
import { useOrganizationsStore } from '~/store/organizations'
import { CloudFunctions } from '~/constants/firebase/cloud-functions'
import { getOrganizationRolesOptions } from '~/utils/user/role'
import LucideIcon from '~/components/ui/LucideIcon.vue'

const props = defineProps<{
  visible: boolean
  alreadyUsedEmails: string[]
}>()
const emits = defineEmits(['hide'])

const organizationsStore = useOrganizationsStore()
const { selectedOrganization } = storeToRefs(organizationsStore)

const visibleValue = computed({
  get: () => props.visible,
  set: () => emits('hide'),
})

const schema = computed(() => ({
  groupsToInvite: array().of(
    object().shape({
      emails: array().min(1).of(
        string().email().notOneOf(props.alreadyUsedEmails).required(),
      ),
      role: number().required(),
    }),
  ).strict(),
}))
const initialValues = {
  groupsToInvite: [
    {
      emails: [],
      role: OrganizationRoles.User,
    },
  ],
}

const getFilteredOrganizationRolesOptions = (
  groupsToInvite: InvitationGroup[],
  groupRole?: OrganizationRoles,
) => {
  const selectedGroupsRoles = groupsToInvite.map(({ role }) => role)
  return getOrganizationRolesOptions().filter(
    ({ value }) => groupRole === value || !selectedGroupsRoles.includes(value),
  )
}
const appConfig = useAppConfig() as AppConfig
const inviteMembers = async (values) => {
  const members = values.groupsToInvite
    .map(({ role, emails }) =>
      emails.map(email => ({
        role,
        email: email.toLowerCase(),
        ...appConfig.structuralInformation?.organization?.memberAdditionalDefaultValues,
      })),
    )
    .flat()

  await useHttpsCallable<InviteOrganizationMembersData>(
    CloudFunctions.InviteOrganizationMembers,
    {
      organizationId: selectedOrganization.value!.id,
      members,
    },
  )
  emits('hide')
}
</script>
